/**
 * 调色板配置
 * 基于ERC-1155 Multi-Asset Platform的多元化配色系统
 * 体现多样性、高效率和综合性的视觉特征
 */
// @mui
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS

// ERC-1155 Multi-Asset Colors - 多元化活力配色
const PRIMARY = {
  lighter: '#E8F5E8',
  light: '#66BB6A',
  main: '#4CAF50',  // 绿色 - 象征增长和多样性
  dark: '#388E3C',
  darker: '#2E7D32',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#E3F2FD',
  light: '#42A5F5',
  main: '#2196F3',  // 蓝色 - 象征效率和科技
  dark: '#1976D2',
  darker: '#1565C0',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#E1F5FE',
  light: '#4FC3F7',
  main: '#00BCD4',
  dark: '#0097A7',
  darker: '#006064',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#E8F5E8',
  light: '#81C784',
  main: '#4CAF50',
  dark: '#388E3C',
  darker: '#2E7D32',
  contrastText: '#FFFFFF',
};

// ERC-1155 专属多元化色彩
const MULTI = {
  lighter: '#FFF3E0',
  light: '#FFB74D',
  main: '#FF9800',  // 橙色 - 象征活力和创新
  dark: '#F57C00',
  darker: '#E65100',
  contrastText: '#FFFFFF',
};

const DIVERSE = {
  lighter: '#F3E5F5',
  light: '#BA68C8',
  main: '#9C27B0',  // 紫色 - 象征多样性和包容性
  dark: '#7B1FA2',
  darker: '#6A1B9A',
  contrastText: '#FFFFFF',
};

const PREMIUM = {
  lighter: '#fffbeb',
  light: '#fed7aa',
  main: '#ea580c',
  dark: '#c2410c',
  darker: '#9a3412',
  contrastText: '#fff',
};

const LUXURY = {
  lighter: '#fdf4ff',
  light: '#e879f9',
  main: '#d946ef',
  dark: '#c026d3',
  darker: '#a21caf',
  contrastText: '#fff',
};

const RARE = {
  lighter: '#f0f9ff',
  light: '#7dd3fc',
  main: '#0ea5e9',
  dark: '#0284c7',
  darker: '#0369a1',
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#FFF8E1',
  light: '#FFD54F',
  main: '#FFC107',
  dark: '#FFA000',
  darker: '#FF8F00',
  contrastText: '#000000',
};

const ERROR = {
  lighter: '#FFEBEE',
  light: '#EF5350',
  main: '#F44336',
  dark: '#D32F2F',
  darker: '#C62828',
  contrastText: '#FFFFFF',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

// ERC-1155 专属多彩渐变色彩
const GRADIENTS = {
  // 主要渐变 - 绿色增长感
  primary: `linear-gradient(135deg, ${PRIMARY.light} 0%, ${PRIMARY.main} 50%, ${PRIMARY.dark} 100%)`,
  // 次要渐变 - 蓝色效率感
  secondary: `linear-gradient(135deg, ${SECONDARY.light} 0%, ${SECONDARY.main} 50%, ${SECONDARY.dark} 100%)`,
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  // ERC-1155 Multi-Asset Gradients
  brand: `linear-gradient(135deg, ${PRIMARY.main}, ${SECONDARY.main})`,
  brandReverse: `linear-gradient(135deg, ${SECONDARY.main}, ${PRIMARY.main})`,
  subtle: `linear-gradient(135deg, ${PRIMARY.lighter}, ${SECONDARY.lighter})`,
  // 多元渐变 - 橙绿配色
  multi: `linear-gradient(135deg, ${MULTI.main} 0%, ${PRIMARY.main} 100%)`,
  // 多样渐变 - 紫蓝配色
  diverse: `linear-gradient(135deg, ${DIVERSE.main} 0%, ${SECONDARY.main} 100%)`,
  // 综合渐变 - 多彩效果
  comprehensive: `linear-gradient(135deg, ${PRIMARY.main} 0%, ${SECONDARY.main} 25%, ${MULTI.main} 50%, ${DIVERSE.main} 75%, ${INFO.main} 100%)`,
  // 批量渐变 - 效率感
  batch: `linear-gradient(135deg, ${SECONDARY.main} 0%, ${INFO.main} 50%, ${PRIMARY.main} 100%)`,
  premium: `linear-gradient(135deg, ${PREMIUM.main}, ${PRIMARY.main})`,
  luxury: `linear-gradient(135deg, ${LUXURY.main}, ${SECONDARY.main})`,
  rare: `linear-gradient(135deg, ${RARE.main}, ${INFO.main})`,
  golden: `linear-gradient(135deg, #fbbf24, #f59e0b, #d97706)`,
  royal: `linear-gradient(135deg, #8b5cf6, #7c3aed, #5b21b6)`,
};

const CHART_COLORS = {
  violet: ['#9C27B0', '#BA68C8', '#CE93D8', '#F3E5F5'],
  blue: ['#2196F3', '#42A5F5', '#64B5F6', '#E3F2FD'],
  green: ['#4CAF50', '#66BB6A', '#81C784', '#E8F5E8'],
  orange: ['#FF9800', '#FFB74D', '#FFCC02', '#FFF3E0'],
  red: ['#F44336', '#EF5350', '#E57373', '#FFEBEE'],
  // ERC-1155 Multi-Asset Chart Colors
  multi: ['#FF9800', '#FFB74D', '#FFCC02', '#FFF3E0'],
  diverse: ['#9C27B0', '#BA68C8', '#CE93D8', '#F3E5F5'],
  comprehensive: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0'],
  batch: ['#2196F3', '#00BCD4', '#4CAF50', '#E3F2FD'],
  // Legacy ERC-721 Colors for compatibility
  gold: ['#f59e0b', '#fbbf24', '#fed7aa', '#fffbeb'],
  purple: ['#8b5cf6', '#c084fc', '#e879f9', '#fdf4ff'],
  premium: ['#ea580c', '#fed7aa', '#fffbeb', '#fff7ed'],
  luxury: ['#d946ef', '#e879f9', '#f0abfc', '#fdf4ff'],
  rare: ['#0ea5e9', '#7dd3fc', '#bae6fd', '#f0f9ff'],
};

const COMMON = {
  common: { black: '#161C24', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#FFFFFF' },
  secondary: { ...SECONDARY, contrastText: '#FFFFFF' },
  info: { ...INFO, contrastText: '#FFFFFF' },
  success: { ...SUCCESS, contrastText: '#FFFFFF' },
  warning: { ...WARNING, contrastText: '#000000' },
  error: { ...ERROR, contrastText: '#FFFFFF' },
  // ERC-1155 Specific Colors
  multi: { ...MULTI, contrastText: '#FFFFFF' },
  diverse: { ...DIVERSE, contrastText: '#FFFFFF' },
  // Legacy ERC-721 Colors for compatibility
  premium: { ...PREMIUM, contrastText: '#fff' },
  luxury: { ...LUXURY, contrastText: '#fff' },
  rare: { ...RARE, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { 
      primary: GREY[800], 
      secondary: GREY[600], 
      disabled: GREY[500] 
    },
    background: { 
      paper: '#fff', 
      default: '#fff', 
      neutral: GREY[100],
      // ERC-1155 Multi-Asset Brand Background
      brand: `linear-gradient(135deg, ${alpha(PRIMARY.main, 0.05)}, ${alpha(SECONDARY.main, 0.05)})`,
      multi: `linear-gradient(135deg, ${alpha(MULTI.main, 0.03)}, ${alpha(PRIMARY.main, 0.03)})`,
      diverse: `linear-gradient(135deg, ${alpha(DIVERSE.main, 0.03)}, ${alpha(SECONDARY.main, 0.03)})`,
      comprehensive: `linear-gradient(135deg, ${alpha(PRIMARY.main, 0.02)}, ${alpha(SECONDARY.main, 0.02)}, ${alpha(MULTI.main, 0.02)})`,
      // Legacy ERC-721 backgrounds for compatibility
      premium: `linear-gradient(135deg, ${alpha(PREMIUM.main, 0.03)}, ${alpha(PRIMARY.main, 0.03)})`,
      luxury: `linear-gradient(135deg, ${alpha(LUXURY.main, 0.03)}, ${alpha(SECONDARY.main, 0.03)})`,
      rare: `linear-gradient(135deg, ${alpha(RARE.main, 0.03)}, ${alpha(INFO.main, 0.03)})`,
    },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { 
      primary: '#fff', 
      secondary: GREY[400], 
      disabled: GREY[600] 
    },
    background: { 
      paper: GREY[800], 
      default: GREY[900], 
      neutral: GREY[500_16],
      // ERC-721 Premium Dark Background
      brand: `linear-gradient(135deg, ${alpha(PRIMARY.dark, 0.1)}, ${alpha(SECONDARY.dark, 0.1)})`,
      premium: `linear-gradient(135deg, ${alpha(PREMIUM.dark, 0.08)}, ${alpha(PRIMARY.dark, 0.08)})`,
      luxury: `linear-gradient(135deg, ${alpha(LUXURY.dark, 0.08)}, ${alpha(SECONDARY.dark, 0.08)})`,
      rare: `linear-gradient(135deg, ${alpha(RARE.dark, 0.08)}, ${alpha(INFO.dark, 0.08)})`,
    },
    action: { active: GREY[500], ...COMMON.action },
  },
};

export default palette;